'use client';
import React, { FC } from 'react';
import { faFileAlt } from '@fortawesome/pro-solid-svg-icons/faFileAlt';
import { faSitemap } from '@fortawesome/pro-solid-svg-icons/faSitemap';
import { faUserDoctor } from '@fortawesome/pro-solid-svg-icons/faUserDoctor';
import { useIntl } from 'react-intl';
import { ComposerNotesArgsProps } from '@osmosis/shared/types/learndb/learnData';
import {
  ComposerNoteGroup,
  ComposerNotesDataType,
  selectComposerNotesForProgram,
} from '@osmosis/shared/types/notes';
import LearnBlock, { CommonLearnBlockProps } from '../LearnBlock';
import NotesComposerContent from './NotesComposerContent';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import NotesComposerPrintButton from './NotesComposerPrintButton';
import { useLearnAccess } from '../../../../context/LearnAccessContext';
import { useUserContext } from '../../../../context/UserContext';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';

type Props = {
  notes: ComposerNotesArgsProps;
  learnNodeID: number;
  learnUrl: string;
  /**
   * Limits printing to one group of the node's notes. Set by the Learn tabs, so
   * a tab prints only the note it shows; omitted in the stacked layout, which
   * shows (and prints) every note the user may see.
   */
  printGroup?: ComposerNoteGroup;
} & CommonLearnBlockProps;

/**
 * Composer Notes Block component.
 */
const NotesComposerBlock: FC<Props> = ({
  notes,
  learnNodeID,
  printGroup,
  contentOnly,
}) => {
  const intl = useIntl();

  const NOTE_MAP = {
    [ComposerNotesDataType.DMT]: {
      icon: faSitemap,
      title: intl.formatMessage({
        defaultMessage: 'Decision-Making Tree',
        id: 'learn.notes_block.type.dmt',
      }),
    },
    [ComposerNotesDataType.NOTE]: {
      icon: faFileAlt,
      title: intl.formatMessage({
        id: 'learn.notes_block.type.notes',
        defaultMessage: 'Notes',
      }),
    },
    [ComposerNotesDataType.OMM]: {
      icon: faUserDoctor,
      title: intl.formatMessage({
        id: 'learn.notes_block.type.omm',
        defaultMessage: 'Osteopathic Considerations',
      }),
    },
  };

  const getTitle = (notesType?: ComposerNotesDataType) =>
    NOTE_MAP[notesType || ComposerNotesDataType.NOTE]?.title ||
    NOTE_MAP.note.title;

  const getIcon = (notesType?: ComposerNotesDataType) =>
    NOTE_MAP[notesType || ComposerNotesDataType.NOTE]?.icon ||
    NOTE_MAP.note.icon;

  const { hasDMTAccess, isDemoLearn, hasAccess, denyReason } = useLearnAccess();
  const { user, isUserLoaded } = useUserContext();

  const hasBlurredBlock = Boolean(denyReason);

  // A node can have both a shared note (or DMT) and an OMM/Osteopathic
  // Considerations note. Render every note the user's program allows, OMM last,
  // so a DO user gets the osteopathic note *below* the shared one rather than
  // instead of it. Gating happens client-side because the Learn page is
  // rendered user-agnostic and the user is hydrated on the client. Fail closed:
  // hidden until an allowed program is confirmed.
  const candidates = notes.allNotes?.length ? notes.allNotes : [notes];

  // When several notes exist, which of them render depends on the user's
  // program, and that isn't known until the user loads on the client. Wait for
  // it — otherwise a non-DO user briefly sees the DO-only note. A single note
  // is gated the same way but can't change composition, so it renders straight
  // away (an OMM-only node still resolves to nothing for a non-DO user).
  if (candidates.length > 1 && !isUserLoaded) {
    return null;
  }

  const selectedNotes = selectComposerNotesForProgram(
    candidates,
    user?.prime?.product,
  );

  if (!selectedNotes.length) {
    return null;
  }

  return (
    <>
      {selectedNotes.map((note, index) => {
        const isDMT = note.type === ComposerNotesDataType.DMT;

        return (
          <LearnBlock
            key={`${note.type ?? ComposerNotesDataType.NOTE}-${index}`}
            title={getTitle(note.type)}
            icon={getIcon(note.type)}
            content={
              <Box
                className={cn(
                  isDMT ? 'min-h-[80vh]' : '',
                  hasDMTAccess || isDemoLearn
                    ? 'overflow-auto'
                    : 'overflow-hidden',
                  contentOnly && !hasBlurredBlock
                    ? 'max-h-full min-h-full'
                    : 'max-h-[80vh]',
                )}
              >
                <NotesComposerContent
                  notes={note}
                  hasAccess={hasAccess}
                  isDemoLearn={isDemoLearn}
                />
              </Box>
            }
            // One print button per block group: the print route renders every
            // note in the group, so a second button would print the same
            // document.
            controls={
              index === 0 ? (
                <NotesComposerPrintButton
                  learnNodeID={learnNodeID}
                  group={printGroup}
                />
              ) : undefined
            }
            withBlurredBlock={hasBlurredBlock}
            hasDMTAccess={hasDMTAccess}
            blurredBlockType={note.type}
            denyReason={denyReason}
            resource="Notes block"
            // In a Learn tab the tab itself names the first note, so that block
            // stays headerless; any note stacked below it keeps its own heading
            // to stay distinguishable.
            contentOnly={contentOnly && index === 0}
            // In the tab layout the panel has no gap of its own; the stacked
            // layout (Main) already spaces its blocks.
            classNames={index > 0 && contentOnly ? { container: 'mt-4' } : {}}
          />
        );
      })}
    </>
  );
};

export default NotesComposerBlock;
